import React from 'react';
import Link from 'next/link';
import { MapPin, Phone, Mail } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

const FacebookIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);

const InstagramIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

const TwitterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const socialIcons: React.FC[] = [FacebookIcon, InstagramIcon, TwitterIcon];

export default function PublicFooter() {
  return (
    <footer className="bg-navy-800 border-t border-white/8 mt-auto">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2.5 mb-4">
              <AppLogo size={36} />
              <span className="font-display font-bold text-xl">
                <span className="text-white">Zip</span>
                <span className="text-gradient">Ad</span>
                <span className="text-white">Book</span>
              </span>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed max-w-sm mb-4">
              The hyperlocal coupon book reaching 1,000+ businesses per region every 5 weeks across Florida and Puerto Rico.
            </p>
            <div className="flex items-center gap-3">
              {socialIcons?.map((SocialIcon: React.FC, i) => (
                <button
                  key={`social-${i}`}
                  className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:border-brand-500/40 transition-all"
                >
                  {React.createElement(SocialIcon)}
                </button>
              ))}
            </div>
          </div>

          {/* Links */}
          <div>
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">Advertise</h4>
            <ul className="space-y-2.5">
              {[
                { label: 'Book an Ad', href: '/order-wizard' },
                { label: 'Placements', href: '/placements' },
                { label: 'Regions & Pricing', href: '/regions-pricing' },
                { label: 'How It Works', href: '/home#how-it-works' },
                { label: 'Customer Support', href: '/customer-support' },
              ]?.map((link) => (
                <li key={`footer-link-${link?.label}`}>
                  <Link href={link?.href} className="text-sm text-slate-400 hover:text-white transition-colors">
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-2.5 text-sm text-slate-400">
                <Phone size={14} className="text-brand-400 shrink-0" />
                <a href="tel:+13212653177" className="hover:text-white transition-colors">+1 (321) 265-3177</a>
              </li>
              <li className="flex items-center gap-2.5 text-sm text-slate-400">
                <Mail size={14} className="text-brand-400 shrink-0" />
                <a href="mailto:info@zipadbook.com" className="hover:text-white transition-colors">info@zipadbook.com</a>
              </li>
              <li className="flex items-center gap-2.5 text-sm text-slate-400">
                <Mail size={14} className="text-brand-400 shrink-0" />
                <a href="mailto:support@zipadbook.com" className="hover:text-white transition-colors">support@zipadbook.com</a>
              </li>
              <li className="flex items-center gap-2.5 text-sm text-slate-400">
                <Mail size={14} className="text-brand-400 shrink-0" />
                <a href="mailto:design@zipadbook.com" className="hover:text-white transition-colors">design@zipadbook.com</a>
              </li>
              <li className="flex items-start gap-2.5 text-sm text-slate-400">
                <MapPin size={14} className="text-brand-400 shrink-0 mt-0.5" />
                Florida & Puerto Rico Coverage
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-6 border-t border-white/8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-500">© 2026 ZipAdBook. All rights reserved. A Take a Zip publication.</p>
          <div className="flex items-center gap-4">
            <Link href="/privacy-policy" className="text-xs text-slate-500 hover:text-slate-300 transition-colors">Privacy Policy</Link>
            <Link href="/terms-of-service" className="text-xs text-slate-500 hover:text-slate-300 transition-colors">Terms of Service</Link>
            <Link href="/customer-support" className="text-xs text-slate-500 hover:text-slate-300 transition-colors">Support</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}